import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col, Badge, ButtonGroup, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faExclamationTriangle, faFileAlt, faStamp, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DocumentReviewModalProps {
  show: boolean;
  onHide: () => void;
  document: {
    id: string;
    filename: string;
    document_type?: string;
    description?: string;
    s3_key?: string;
    file_url?: string;
    s3_url?: string;
    transaction_id?: string;
    broker_review_status?: string | null;
    broker_compliance_score?: number | null;
    broker_reviewed_at?: string | null;
    broker_review_notes?: string | null;
    ai_compliance_analysis?: {
      compliance_score?: number;
      confidence_score?: number;
      risk_level?: string;
      processing_time_ms?: number;
      analyzed_at?: string;
      signatures_detected?: {
        count: number;
        parties: string[];
        missing: string[];
      };
      extracted_data?: any;
      compliance_issues?: Array<{
        type: string;
        severity: string;
        description: string;
        location?: string;
      }>;
      missing_fields?: string[];
    };
  };
  onReviewComplete: () => void;
}

const DocumentReviewModal: React.FC<DocumentReviewModalProps> = ({
  show,
  onHide,
  document,
  onReviewComplete
}) => {
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewStatus, setReviewStatus] = useState<'approved' | 'needs_revision' | 'rejected'>('approved');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(true);
  
  // New states for stamp functionality
  const [applyStamp, setApplyStamp] = useState(true);
  const [stampDate, setStampDate] = useState<Date>(new Date());
  const [stampPosition, setStampPosition] = useState<{ type: string; page?: number | string }>({ 
    type: 'bottom-right', 
    page: 1 
  });
  const [fileNumber, setFileNumber] = useState<string>('');
  const [showStampOptions, setShowStampOptions] = useState(false);

  useEffect(() => {
    if (show && document.id) {
      fetchDocumentUrl();
    }
  }, [show, document.id]);

  const fetchDocumentUrl = async () => {
    try {
      setLoadingDoc(true);
      console.log('📄 Fetching document URL for:', {
        id: document.id,
        s3_key: document.s3_key,
        file_url: document.file_url,
        fullDocument: document
      });
      
      // First check if we have a direct URL
      if (document.file_url && document.file_url.startsWith('http')) {
        console.log('✅ Using existing file_url');
        setDocumentUrl(document.file_url);
        setLoadingDoc(false);
        return;
      }
      
      // Check for s3_url as well
      if (document.s3_url && document.s3_url.startsWith('http')) {
        console.log('✅ Using existing s3_url');
        setDocumentUrl(document.s3_url);
        setLoadingDoc(false);
        return;
      }
      
      // Try to get a presigned URL for the document
      const response = await axiosInstance.get(`/documents/presigned-url/${document.id}`);
      
      if (response.data.success && response.data.url) {
        console.log('✅ Got presigned URL');
        setDocumentUrl(response.data.url);
      } else {
        throw new Error('No presigned URL received');
      }
    } catch (err) {
      console.error('Error fetching document URL:', err);
      
      // Try alternative endpoint for broker documents
      try {
        const altResponse = await axiosInstance.post('/broker-review/get-document-url', {
          document_id: document.id
        });
        
        if (altResponse.data.success && altResponse.data.url) {
          console.log('✅ Got URL from broker endpoint');
          setDocumentUrl(altResponse.data.url);
        } else if (document.file_url) {
          // Use fallback URL if available
          setDocumentUrl(document.file_url);
        }
      } catch (altErr) {
        console.error('Alternative fetch also failed:', altErr);
        if (document.file_url) {
          setDocumentUrl(document.file_url);
        }
      }
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleSubmitReview = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, submit the review
      const reviewResponse = await axiosInstance.post('/broker-review/review-document', {
        document_id: document.id,
        review_status: reviewStatus,
        review_notes: reviewNotes.trim()
      });

      if (!reviewResponse.data.success) {
        setError(reviewResponse.data.error || 'Failed to submit review');
        return;
      }

      // If approved and stamping is enabled, apply the stamp
      if (reviewStatus === 'approved' && applyStamp) {
        try {
          const stampResponse = await axiosInstance.post('/broker-review/stamp-document', {
            documentId: document.id,
            customDate: stampDate,
            stampOptions: {
              position: stampPosition,
              page: stampPosition.page || 1
            },
            fileNumber: fileNumber || undefined
          });

          if (!stampResponse.data.success) {
            console.warn('Review submitted but stamping failed:', stampResponse.data.error);
            // Don't fail the entire process if stamping fails
          } else {
            console.log('Document stamped successfully:', stampResponse.data.stampedDocument);
          }
        } catch (stampErr) {
          console.warn('Review submitted but stamping failed:', stampErr);
          // Continue with success - stamping failure doesn't fail the review
        }
      }

      onReviewComplete();
    } catch (err: any) {
      console.error('Error submitting review:', err);
      setError(err.response?.data?.error || 'Failed to submit review. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
      case 'needs_revision':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" />;
      case 'rejected':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />;
      default:
        return null;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faFileAlt} className="me-2" />
          Review Document: {document.filename}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row className="mb-3">
          <Col md={6}>
            <small className="text-muted">Document Type</small>
            <div>{document.document_type || 'Not specified'}</div>
          </Col>
          <Col md={6}>
            <small className="text-muted">Current Status</small>
            <div>
              {document.broker_review_status ? (
                <Badge bg={
                  document.broker_review_status === 'approved' ? 'success' :
                  document.broker_review_status === 'needs_revision' ? 'warning' :
                  document.broker_review_status === 'rejected' ? 'danger' : 'secondary'
                }>
                  {document.broker_review_status.replace('_', ' ').charAt(0).toUpperCase() + 
                   document.broker_review_status.replace('_', ' ').slice(1)}
                </Badge>
              ) : (
                <Badge bg="secondary">Not Reviewed</Badge>
              )}
            </div>
          </Col>
        </Row>

        {document.description && (
          <div className="mb-3">
            <small className="text-muted">Description</small>
            <div>{document.description}</div>
          </div>
        )}

        {/* AI Analysis Results */}
        {document.ai_compliance_analysis && (
          <div className="mb-4 p-3 border rounded">
            <h6 className="mb-3 text-primary">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              AI Analysis Results
            </h6>
            
            <Row className="mb-3">
              <Col md={3}>
                <small className="text-muted">Compliance Score</small>
                <div className="h4 mb-0">
                  <Badge bg={
                    (document.ai_compliance_analysis.compliance_score || 0) >= 90 ? 'success' :
                    (document.ai_compliance_analysis.compliance_score || 0) >= 70 ? 'warning' : 'danger'
                  }>
                    {document.ai_compliance_analysis.compliance_score || 0}%
                  </Badge>
                </div>
              </Col>
              <Col md={3}>
                <small className="text-muted">Risk Assessment</small>
                <div>
                  <Badge bg={
                    document.ai_compliance_analysis.risk_level === 'low' ? 'success' :
                    document.ai_compliance_analysis.risk_level === 'medium' ? 'warning' :
                    document.ai_compliance_analysis.risk_level === 'high' ? 'danger' : 'dark'
                  }>
                    {(document.ai_compliance_analysis.risk_level || 'unknown').toUpperCase()} RISK
                  </Badge>
                </div>
              </Col>
              <Col md={3}>
                <small className="text-muted">Confidence Score</small>
                <div>{document.ai_compliance_analysis.confidence_score || 0}%</div>
              </Col>
              <Col md={3}>
                <small className="text-muted">Processing Time</small>
                <div>{document.ai_compliance_analysis.processing_time_ms || 0}ms</div>
              </Col>
            </Row>

            {/* Compliance Issues */}
            {document.ai_compliance_analysis.compliance_issues && document.ai_compliance_analysis.compliance_issues.length > 0 && (
              <div className="mb-3">
                <small className="text-muted">Compliance Issues</small>
                <ul className="mb-0 mt-1">
                  {document.ai_compliance_analysis.compliance_issues.map((issue, idx) => (
                    <li key={idx}>
                      <Badge bg={issue.severity === 'critical' ? 'danger' : issue.severity === 'high' ? 'warning' : 'info'} className="me-2">
                        {issue.severity}
                      </Badge>
                      {issue.description}
                      {issue.location && <small className="text-muted"> (Location: {issue.location})</small>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Signatures Analysis */}
            {document.ai_compliance_analysis.signatures_detected && (
              <div className="mb-3">
                <small className="text-muted">Signatures Analysis</small>
                <div className="mt-1">
                  <div>{document.ai_compliance_analysis.signatures_detected.count} Signatures Detected</div>
                  {document.ai_compliance_analysis.signatures_detected.parties.length > 0 && (
                    <div className="mt-1">
                      {document.ai_compliance_analysis.signatures_detected.parties.map((party, idx) => (
                        <Badge key={idx} bg="secondary" className="me-1">{party}</Badge>
                      ))}
                    </div>
                  )}
                  {document.ai_compliance_analysis.signatures_detected.missing.length > 0 && (
                    <Alert variant="warning" className="mt-2 mb-0 py-1 px-2">
                      <small>Missing signatures: {document.ai_compliance_analysis.signatures_detected.missing.join(', ')}</small>
                    </Alert>
                  )}
                </div>
              </div>
            )}

            {/* Extracted Data */}
            {document.ai_compliance_analysis.extracted_data && Object.keys(document.ai_compliance_analysis.extracted_data).length > 0 && (
              <div>
                <small className="text-muted">Extracted Data</small>
                <div className="mt-1">
                  {Object.entries(document.ai_compliance_analysis.extracted_data).map(([key, value], idx) => (
                    <div key={idx}>
                      <strong>{key.replace(/_/g, ' ').charAt(0).toUpperCase() + key.replace(/_/g, ' ').slice(1)}:</strong> {String(value)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Processing Metrics */}
            {document.ai_compliance_analysis.analyzed_at && (
              <div className="mt-2">
                <small className="text-muted">Analyzed: {new Date(document.ai_compliance_analysis.analyzed_at).toLocaleString()}</small>
              </div>
            )}
          </div>
        )}

        {/* Document Preview */}
        <div className="mb-4 p-3 bg-light rounded">
          <h6 className="mb-3">Document Preview</h6>
          {loadingDoc ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading document...</p>
            </div>
          ) : (
            <div>
              {documentUrl ? (
                <>
                  <div className="border rounded overflow-hidden" style={{ height: '400px' }}>
                    <iframe
                      src={documentUrl}
                      width="100%"
                      height="100%"
                      title="Document Preview"
                      style={{ border: 'none' }}
                      onError={(e) => {
                        console.error('Iframe load error:', e);
                        setDocumentUrl(null);
                      }}
                    />
                  </div>
                  <div className="mt-2 text-center">
                    <a 
                      href={documentUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                      Open Full Document
                    </a>
                  </div>
                </>
              ) : (
                <Alert variant="info">
                  <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                  Document preview not available. You can still review and make a decision based on the document information above.
                </Alert>
              )}
            </div>
          )}
        </div>

        {/* Review Form */}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Review Decision</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                id="status-approved"
                label={
                  <>
                    {getStatusIcon('approved')} Approve
                  </>
                }
                value="approved"
                checked={reviewStatus === 'approved'}
                onChange={() => setReviewStatus('approved')}
              />
              <Form.Check
                inline
                type="radio"
                id="status-revision"
                label={
                  <>
                    {getStatusIcon('needs_revision')} Request Revision
                  </>
                }
                value="needs_revision"
                checked={reviewStatus === 'needs_revision'}
                onChange={() => setReviewStatus('needs_revision')}
              />
              <Form.Check
                inline
                type="radio"
                id="status-rejected"
                label={
                  <>
                    {getStatusIcon('rejected')} Reject
                  </>
                }
                value="rejected"
                checked={reviewStatus === 'rejected'}
                onChange={() => setReviewStatus('rejected')}
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Review Notes 
              {reviewStatus !== 'approved' && <span className="text-danger"> *</span>}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder={
                reviewStatus === 'approved' 
                  ? 'Optional: Add any notes about this document...'
                  : 'Required: Explain what needs to be revised or why the document is rejected...'
              }
              required={reviewStatus !== 'approved'}
            />
          </Form.Group>

          {/* Stamp Options - Show only when approving */}
          {reviewStatus === 'approved' && (
            <Card className="mb-3 border-success">
              <Card.Header className="bg-success text-white d-flex align-items-center">
                <FontAwesomeIcon icon={faStamp} className="me-2" />
                Broker Approval Stamp
                <Form.Check 
                  type="switch"
                  id="apply-stamp-switch"
                  label="Apply Stamp"
                  checked={applyStamp}
                  onChange={(e) => setApplyStamp(e.target.checked)}
                  className="ms-auto text-white"
                />
              </Card.Header>
              
              {applyStamp && (
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                          Stamp Date
                        </Form.Label>
                        <div>
                          <DatePicker
                            selected={stampDate}
                            onChange={(date: Date) => setStampDate(date)}
                            maxDate={new Date()}
                            dateFormat="MM/dd/yyyy"
                            className="form-control"
                            placeholderText="Select stamp date"
                          />
                        </div>
                        <Form.Text className="text-muted">
                          Date that will appear on the stamp (can be backdated)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>File Number (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          value={fileNumber}
                          onChange={(e) => setFileNumber(e.target.value)}
                          placeholder="Auto-generated if empty"
                        />
                        <Form.Text className="text-muted">
                          Leave empty to auto-generate
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Stamp Position</Form.Label>
                    <ButtonGroup className="d-block">
                      {[
                        { key: 'bottom-right', label: 'Bottom Right (Default)' },
                        { key: 'top-right', label: 'Top Right' },
                        { key: 'bottom-left', label: 'Bottom Left' },
                        { key: 'center-bottom', label: 'Center Bottom' }
                      ].map((position) => (
                        <Button
                          key={position.key}
                          variant={stampPosition.type === position.key ? 'primary' : 'outline-primary'}
                          size="sm"
                          className="me-2 mb-2"
                          onClick={() => setStampPosition({ ...stampPosition, type: position.key })}
                        >
                          {position.label}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Stamp Page</Form.Label>
                    <Form.Select
                      value={stampPosition.page}
                      onChange={(e) => setStampPosition({ ...stampPosition, page: e.target.value })}
                    >
                      <option value={1}>First Page</option>
                      <option value="last">Last Page</option>
                      <option value="all">All Pages</option>
                    </Form.Select>
                  </Form.Group>

                  <Alert variant="info" className="mb-0">
                    <small>
                      <strong>Preview:</strong> The stamp will appear as a bordered box with:
                      <br />• BROKER APPROVED
                      <br />• File #: {fileNumber || 'Auto-generated'}
                      <br />• By: [Your Initials]
                      <br />• Date: {stampDate.toLocaleDateString()}
                    </small>
                  </Alert>
                </Card.Body>
              )}
            </Card>
          )}

          {reviewStatus === 'needs_revision' && (
            <Alert variant="info">
              <strong>Note:</strong> The agent will be notified to upload a revised document. 
              Please be specific about what changes are needed.
            </Alert>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant={
            reviewStatus === 'approved' ? 'success' : 
            reviewStatus === 'needs_revision' ? 'warning' : 'danger'
          }
          onClick={handleSubmitReview}
          disabled={loading || (reviewStatus !== 'approved' && !reviewNotes.trim())}
        >
          {loading ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              {reviewStatus === 'approved' && applyStamp ? 'Approving & Stamping...' : 'Submitting...'}
            </>
          ) : (
            <>
              {reviewStatus === 'approved' && applyStamp ? (
                <>
                  <FontAwesomeIcon icon={faStamp} className="me-1" />
                  Approve & Apply Stamp
                </>
              ) : (
                <>
                  {getStatusIcon(reviewStatus)} Submit Review
                </>
              )}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentReviewModal;